import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Users, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Courses = () => {
  const navigate = useNavigate();
  
  const courses = [
    {
      id: 'drones',
      title: 'Drone Technology',
      description: 'Master drone operations, maintenance, and applications in various industries.',
      duration: '12 weeks',
      students: '20 per batch',
      image: 'https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&q=80&w=400',
      features: [
        'Drone flight mechanics',
        'Maintenance and repair',
        'Industrial applications',
        'Safety regulations'
      ]
    },
    {
      id: 'embedded',
      title: 'Embedded Systems',
      description: 'Learn microcontroller programming and embedded system design.',
      duration: '16 weeks',
      students: '15 per batch',
      image: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?auto=format&fit=crop&q=80&w=400',
      features: [
        'Microcontroller programming',
        'Circuit design',
        'IoT applications',
        'Real-time systems'
      ]
    },
    {
      id: 'frontend',
      title: 'Frontend Development',
      description: 'Master modern web development technologies and frameworks.',
      duration: '14 weeks',
      students: '25 per batch',
      image: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613?auto=format&fit=crop&q=80&w=400',
      features: [
        'HTML5 & CSS3',
        'JavaScript/TypeScript',
        'React & Next.js',
        'Responsive design'
      ]
    },
    {
      id: 'hacking',
      title: 'Ethical Hacking',
      description: 'Learn cybersecurity fundamentals and ethical hacking techniques.',
      duration: '10 weeks',
      students: '20 per batch',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=400',
      features: [
        'Network security',
        'Penetration testing',
        'Security tools',
        'Best practices'
      ]
    }
  ];

  const handleEnroll = (courseId: string) => {
    navigate(`/courses/${courseId}`);
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-200 mb-4">Our Courses</h1>
          <p className="text-xl text-gray-400">Explore our comprehensive technology courses</p>
        </motion.div>

        <div className="space-y-16">
          {courses.map((course, index) => (
            <motion.div
              key={course.id}
              id={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <div className="md:flex">
                <div className="md:w-1/3">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{course.title}</h2>
                  <p className="text-gray-600 mb-6">{course.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-blue-600 mr-2" />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-blue-600 mr-2" />
                      <span>{course.students}</span>
                    </div>
                    <div className="flex items-center">
                      <Award className="h-5 w-5 text-blue-600 mr-2" />
                      <span>Certificate</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">What you'll learn:</h3>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {course.features.map((feature, i) => (
                        <li key={i} className="flex items-center">
                          <ArrowRight className="h-4 w-4 text-blue-600 mr-2" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => handleEnroll(course.id)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition-colors"
                  >
                    View Details & Enroll
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Courses;